import React, { FC, MouseEvent, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import scrollIntoView from 'ia-react-core/~/utils/dom/scrollIntoView';
import { NestedFormInfo } from 'ia-react-core/~/interfaces/NestedFormInfo';
import RootContext from '../../config/_RootContext';
import Link from '../../navigation/Link';
import FormContext from '../FormContext';
import ControlGroupContext from '../_ControlGroupContext';
import ControlMessageContext from '../_ControlMessageContext';

export interface ControlAnchorProps {
  name: string;
  children?: ReactNode;
  formScope?: string[];
}

const ControlAnchor: FC<ControlAnchorProps> = ({ name, children, formScope }) => {
  const controlMessageContext = useContext(ControlMessageContext);

  const resolvedName = useMemo(() => name || controlMessageContext?.name, [controlMessageContext?.name, name]);
  const resolvedFormScope = useMemo(() => formScope || controlMessageContext?.formScope?.slice(1), [controlMessageContext?.formScope, formScope]);

  const { controls } = useContext(ControlGroupContext);

  const { nestedFormInfos, nestedControlInfos, watchRegistrations } = useContext(FormContext);

  const findFormInfo = useCallback((formInfos: NestedFormInfo<any>[], currentScope: string[]) => {
    const currentName = currentScope.shift();
    const formInfo = formInfos.find((nestedFormInfo) => nestedFormInfo.name === currentName);

    if (!currentScope.length || !formInfo) return formInfo;

    return findFormInfo(formInfo.nestedFormInfos, currentScope);
  }, []);

  const getControlInfo = useCallback(() => {
    if (controls) {
      const foundControlInfo = controls.find((control) => (
        control.name === resolvedName &&
        (!resolvedFormScope || control.formScope.slice(1).join('__') === resolvedFormScope.join('__'))
      ));

      if (foundControlInfo) {
        return foundControlInfo;
      }
    }

    const controlInfos = !resolvedFormScope?.length ?
      nestedControlInfos :
      findFormInfo(nestedFormInfos, [...resolvedFormScope])?.nestedControlInfos;

    if (controlInfos?.length) {
      const foundControlInfo = controlInfos.find((control) => (
        control.name === resolvedName
      ));

      if (foundControlInfo) return foundControlInfo;
    }

    return { id: null, label: null };
  }, [controls, findFormInfo, resolvedFormScope, resolvedName, nestedControlInfos, nestedFormInfos]);

  const [{ id, label }, setControlInfo] = useState<{ id: string | null; label: ReactNode | null}>(getControlInfo());

  const { root } = useContext(RootContext);

  const handleClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const target: HTMLElement = root.querySelector(`#${id}`);
    scrollIntoView(target);
  }, [id, root]);

  useEffect(() => watchRegistrations(() => {
    setControlInfo(getControlInfo());
  }), [getControlInfo, watchRegistrations]);

  if (!id) {
    return null;
  }

  return (
    <Link href={`#${id}`} onClick={handleClick}>{children || label || resolvedName}</Link>
  );
};

export default ControlAnchor;
